import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { FaArrowLeft, FaCode, FaUniversity, FaCreditCard } from 'react-icons/fa';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import StartSidebar from '../components/StartSidebar';
import PortalBackground from '../components/PortalBackground';
import {
  buildSignerFormDataFromProfile,
  getMissingSignerFields,
  SIGNER_FORM_FIELDS,
  BUSINESS_STRUCTURE_OPTIONS,
} from '../utils/signerProfilePrefill';
import { getNextContractNumber, incrementContractNumber } from '../services/firebase/contractNumberService';
import { getAccountById, updateAccount, getMyAccountSnapshot } from '../services/firestoreService';
import { normalizeCollectionForConversion } from '../components/crm/AccountDocumentsTab';
import { itemDeliverableAppliesToProgram } from '../utils/accountProgramDeliverables';
import {
  createBidFromCatalogItem,
  fetchBidsForAccount,
  fetchPublishedBidsForAccount,
} from '../services/alaCarteBidService';
import { isUnpricedCaseByCaseItem } from '../utils/programPricing';
import { leadConversionFields, programMapWith } from '../utils/accountLifecycle'; // leadSnapshot + convertedToClientAt

const isDevelopment = process.env.NODE_ENV === 'development';

// ---- Start date helpers (module scope so the inline calendar can share them) ----

// Parse YYYY-MM-DD as local date (avoids UTC-midnight timezone shift bug)
const parseLocalDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Format a Date as YYYY-MM-DD in local time (toISOString would shift the day)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format a YYYY-MM-DD string for display, e.g. "Thursday, August 13, 2026"
const formatDisplayDate = (dateStr) =>
  parseLocalDate(dateStr).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

// Check if a date is a weekend
const isWeekend = (date) => {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
};

// The earliest selectable start date: the next weekday after today
const getMinStartDate = () => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  let weekdayCount = 0;
  while (weekdayCount < 1) {
    currentDate.setDate(currentDate.getDate() + 1);
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      weekdayCount++;
    }
  }

  return currentDate;
};

// Check if a date is earlier than the minimum allowed start date
const isBeforeMinStartDate = (date) => {
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < getMinStartDate();
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Full inline month calendar — weekends and dates before the minimum are disabled
const StartDateCalendar = ({ value, onSelect }) => {
  const minDate = getMinStartDate();
  const initialMonth = value ? parseLocalDate(value) : minDate;
  const [viewMonth, setViewMonth] = useState(
    new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1)
  );

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Don't let the user page back before the month that holds the minimum date
  const canGoPrev = new Date(year, month, 1) > new Date(minDate.getFullYear(), minDate.getMonth(), 1);

  const cells = [
    ...Array.from({ length: firstDayOfWeek }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setViewMonth(new Date(year, month - 1, 1))}
          disabled={!canGoPrev}
          aria-label="Previous month"
          className={`p-1.5 rounded-md transition-colors ${
            canGoPrev
              ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
          }`}
        >
          <ChevronLeft size={20} />
        </button>
        <div className="text-sm font-semibold text-gray-900 dark:text-white text-center">
          {viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
        <button
          type="button"
          onClick={() => setViewMonth(new Date(year, month + 1, 1))}
          aria-label="Next month"
          className="p-1.5 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((date, index) => {
          if (!date) return <div key={`empty-${index}`} />;

          const dateStr = formatLocalDate(date);
          const disabled = isWeekend(date) || isBeforeMinStartDate(date);
          const selected = value === dateStr;

          return (
            <button
              key={dateStr}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(dateStr)}
              className={`h-9 rounded-md text-sm font-medium transition-colors ${
                selected
                  ? 'bg-red-600 text-white'
                  : disabled
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-900 dark:text-gray-100 hover:bg-red-50 dark:hover:bg-gray-600'
              }`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Published bid prices for this account keyed by catalog item id (same shape as
 * usePublishedBidPrices). Case-by-case picks with no entry here are still "to be
 * priced" — preliminary, so they stay off the contract and off the deliverables.
 */
const getAccountBidPrices = async (accountId) => {
  if (!accountId) return {};
  try {
    const bids = await fetchPublishedBidsForAccount(accountId);
    const prices = {};
    bids.forEach((bid) => {
      const price = parseFloat(bid?.clientListPrice);
      if (bid?.catalogItemId && Number.isFinite(price) && price > 0) {
        prices[bid.catalogItemId] = price;
      }
    });
    return prices;
  } catch (err) {
    console.error('Error loading published bid prices:', err);
    return {};
  }
};

/**
 * Splits the chosen deliverables into what the contract actually covers versus
 * case-by-case picks still awaiting a bid ("to be priced"). The latter are excluded
 * from the DocuSign program items and the account's tracked deliverables — they go
 * out for bid instead.
 */
const splitToBePricedDeliverables = async (accountId, deliverableIds) => {
  const [catalogSnapshot, bidPrices] = await Promise.all([
    getDocs(collection(db, 'alaCarteItems')),
    getAccountBidPrices(accountId),
  ]);
  const catalogItems = catalogSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  const contractedIds = [];
  const toBePricedItems = [];
  (deliverableIds || []).forEach((itemId) => {
    const item = catalogItems.find((i) => i.id === itemId);
    if (item && isUnpricedCaseByCaseItem(item, bidPrices)) {
      toBePricedItems.push(item);
    } else {
      contractedIds.push(itemId);
    }
  });
  return { contractedIds, toBePricedItems };
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [contractData, setContractData] = useState({
    startDate: '',
    contractLength: '12',
    termsAccepted: false
  });
  const [minimumTerm, setMinimumTerm] = useState(12);
  const [resolvedLeadId, setResolvedLeadId] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isSigningContract, setIsSigningContract] = useState(false);
  const [signerFormData, setSignerFormData] = useState(null);
  // Shown inline when the signer's profile is missing contract fields, so an incomplete
  // profile prompts for the values instead of blocking the signature.
  const [signerFieldInputs, setSignerFieldInputs] = useState({});
  const [showSignerFields, setShowSignerFields] = useState(false);
  const [signerFieldErrors, setSignerFieldErrors] = useState({});

  // Get selected program data from navigation state
  const selectedProgram = location.state?.selectedProgram;
  const selectedTier = location.state?.selectedTier;
  const billingCycle = location.state?.billingCycle;
  const leadId = location.state?.leadId;

  useEffect(() => {
    // Restore from sessionStorage when returning from Stripe (user hit back) - location.state is lost
    const persisted = sessionStorage.getItem('checkoutPersistedData');
    if (persisted && (!selectedProgram || !selectedTier || !billingCycle)) {
      try {
        const data = JSON.parse(persisted);
        sessionStorage.removeItem('checkoutPersistedData');
        sessionStorage.removeItem('checkoutReturnToStep3');
        // Carry EVERYTHING the payment step needs. This path runs after Stripe's cancel/back
        // and used to drop the signed-contract flag and the custom deliverable picks, so the
        // next Pay attempt went to Stripe with no start date (no trial, conversion dated
        // "today") and no deliverables.
        navigate(location.pathname, {
          state: {
            selectedProgram: data.selectedProgram,
            selectedTier: data.selectedTier,
            billingCycle: data.billingCycle,
            leadId: data.leadId,
            contractData: data.contractData,
            contractSigned: Boolean(data.contractSigned),
            customDeliverables: data.customDeliverables,
            customDeliverableQuantities: data.customDeliverableQuantities,
            checkoutReturnedFromStripe: true
          },
          replace: true
        });
        return;
      } catch (e) {
        console.error('Failed to restore checkout data:', e);
        sessionStorage.removeItem('checkoutPersistedData');
      }
    }

    // Redirect if no program data
    if (!selectedProgram || !selectedTier || !billingCycle) {
      const isPortal = location.pathname.startsWith('/portal');
      navigate(isPortal ? '/portal/billing' : '/start/program');
      return;
    }
    
    // Debug logging
    console.log('Checkout page loaded with data:', {
      selectedProgram: selectedProgram?.name,
      selectedTier,
      billingCycle,
      leadId,
      resolvedLeadId,
      programMinimumTerm: selectedProgram?.minimumTerm
    });
    
    // Set minimum term: annual billing always requires 12 months; otherwise use program/Firestore
    if (billingCycle === 'annual') {
      setMinimumTerm(12);
      setContractData(prev => ({
        ...prev,
        contractLength: '12'
      }));
    } else if (selectedProgram?.minimumTerm) {
      const programMinTerm = selectedProgram.minimumTerm;
      setMinimumTerm(programMinTerm);
      setContractData(prev => ({
        ...prev,
        contractLength: programMinTerm.toString()
      }));
    } else {
      loadMinimumTerm();
    }
    
    // Find leadId if not provided
    findLeadId();

    if (currentUser) {
      buildSignerFormDataFromProfile(currentUser)
        .then(setSignerFormData)
        .catch((err) => console.error('Error loading signer profile:', err));
    }
    
    // Hydrate the contract fields from whatever brought us here (DocuSign return or the Stripe
    // back/cancel restore). Only non-empty values win so a stale '' can't blank a real start date.
    const contractSigned = location.state?.contractSigned;
    if (location.state?.contractData) {
      const incoming = Object.fromEntries(
        Object.entries(location.state.contractData).filter(([, v]) => v !== '' && v != null)
      );
      setContractData(prev => ({ ...prev, ...incoming }));
    }
    // Advance to step 3 (payment) when contract is signed
    if (contractSigned) {
      setCurrentStep(3);
    }

    // Check if returning from Stripe checkout (user hit back) - restore to step 3
    if (location.state?.checkoutReturnedFromStripe) {
      setCurrentStep(3);
    }
  }, [selectedProgram, selectedTier, billingCycle, navigate, location.state, leadId, currentUser]);

  const handlePayNow = async (paymentMethod) => {
    if (!selectedProgram || !billingCycle || !paymentMethod) return;

    const baseAmountCents = (() => {
      const base = selectedProgram.pricePerMonth || 0;
      const annualDiscount = selectedTier === 'Custom' ? 0.9 : 0.8; // Custom: 10% off; others: 20% off
      switch (billingCycle) {
        case 'quarterly': return Math.round(base * 3 * 0.9) * 100;
        case 'annual': return Math.round(base * 12 * annualDiscount) * 100;
        default: return base * 100;
      }
    })();

    const isCard = (paymentMethod || 'ach').toLowerCase() === 'card';
    const amountCents = isCard ? Math.round(baseAmountCents * 1.03) : baseAmountCents;

    if (amountCents < 50) return;

    setIsRedirecting(true);
    setCheckoutError(null);

    try {
      // Persist custom program deliverables to the account before redirecting to Stripe
      // so ClientOverview (and billing) can show them after lead→client conversion
      const companyId = leadId || resolvedLeadId;
      const customDeliverables = location.state?.customDeliverables;
      const customDeliverableQuantities = location.state?.customDeliverableQuantities;
      if (companyId && customDeliverables && Array.isArray(customDeliverables) && customDeliverables.length > 0) {
        try {
          // Case-by-case picks with no published bid are preliminary — they never join the
          // tracked deliverables. They're sent out for bid below instead.
          const { contractedIds, toBePricedItems } = await splitToBePricedDeliverables(
            companyId,
            customDeliverables
          );

          if (selectedTier === 'Custom' && contractedIds.length > 0) {
            await updateDoc(doc(db, 'accounts', companyId), {
              customProgramDeliverables: contractedIds,
              customProgramDeliverableQuantities: customDeliverableQuantities || {},
              customProgramUpdatedAt: new Date().toISOString()
            });
          }

          if (toBePricedItems.length > 0) {
            // One bid per item, deduped against anything already on the account, so a
            // Stripe back-and-retry doesn't send the same service out twice.
            const existingBids = await fetchBidsForAccount(companyId);
            const alreadyBid = new Set(
              existingBids.map((b) => b.catalogItemId).filter(Boolean)
            );
            for (const item of toBePricedItems) {
              if (alreadyBid.has(item.id)) continue;
              await createBidFromCatalogItem({
                accountId: companyId,
                catalogItemId: item.id,
                source: 'portal',
                postedByUid: currentUser?.uid || '',
                postToTeam: true,
                quantity: Number(customDeliverableQuantities?.[item.id]) || 1,
                fromProgramSignup: true,
              });
            }
          }
        } catch (persistErr) {
          console.warn('Could not persist custom deliverables to account:', persistErr);
        }
      }

      const createSession = httpsCallable(functions, 'createCheckoutSession');
      const isPortal = location.pathname.startsWith('/portal');
      const returnPath = isPortal ? '/portal/checkout/return' : '/start/checkout/return';

      // startDate from contract (trial until then). The server needs it to trial the subscription
      // until the start date AND to date the lead→client conversion, so never send without it.
      // Component state → navigation state → the lead's own account (the DocuSign return writes
      // contractData there and the webhook writes signedContract) → the Start-flow progress.
      let startDate = contractData?.startDate || location.state?.contractData?.startDate || location.state?.checkoutData?.startDate;
      if (!startDate && companyId) {
        try {
          const acct = await getAccountById(companyId);
          startDate = acct?.contractData?.startDate || acct?.signedContract?.startDate || '';
        } catch (lookupErr) {
          console.warn('Could not read start date from account:', lookupErr);
        }
      }
      if (!startDate && currentUser?.uid) {
        try {
          const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
          startDate = userSnap.exists() ? (userSnap.data()?.startFlowProgress?.programData?.startDate || '') : '';
        } catch (lookupErr) {
          console.warn('Could not read start date from user progress:', lookupErr);
        }
      }
      if (!startDate) {
        setCheckoutError('We could not find your program start date. Please go back to Step 2 and choose it again.');
        setIsRedirecting(false);
        return;
      }
      if (startDate !== contractData?.startDate) {
        setContractData(prev => ({ ...prev, startDate }));
      }

      const { data } = await createSession({
        amount: amountCents,
        paymentMethod: paymentMethod || 'ach',
        currency: 'usd',
        programName: selectedProgram.name,
        billingCycle,
        customerEmail: currentUser?.email,
        returnPath,
        origin: window.location.origin,
        ...(companyId && { companyId }),
        ...(startDate && { startDate }),
        // Rides along in session metadata so the server can set the contract window when it
        // converts this lead to a client after checkout completes.
        contractLength: contractData?.contractLength || String(minimumTerm),
        // What the server prices from (functions/programPricing.js); `amount` above is only
        // compared against the server's figure, never charged.
        tier: selectedTier,
        selectedDeliverables: Array.isArray(location.state?.customDeliverables) ? location.state.customDeliverables : [],
        deliverableQuantities: location.state?.customDeliverableQuantities || {},
      });

      if (data?.url) {
        // Everything needed to retry payment if they back out of Stripe: the signed contract's
        // dates, the signed flag (we're on step 3, so it is), and the custom program picks.
        sessionStorage.setItem('checkoutPersistedData', JSON.stringify({
          selectedProgram,
          selectedTier,
          billingCycle,
          leadId: leadId || resolvedLeadId,
          contractData: { ...contractData, startDate },
          contractSigned: true,
          customDeliverables: location.state?.customDeliverables,
          customDeliverableQuantities: location.state?.customDeliverableQuantities,
        }));
        sessionStorage.setItem('checkoutReturnToStep3', 'true');
        window.location.href = data.url;
      } else {
        setCheckoutError('No checkout URL received');
        setIsRedirecting(false);
      }
    } catch (err) {
      console.error('createCheckoutSession error:', err);
      setCheckoutError(err.message || 'Failed to start checkout');
      setIsRedirecting(false);
    }
  };

  const loadMinimumTerm = async () => {
    try {
      // Fallback: Load minimum term from Firestore settings if program doesn't have it
      const minimumTermDoc = await getDoc(doc(db, 'settings', 'minimumTerm'));
      if (minimumTermDoc.exists()) {
        const data = minimumTermDoc.data();
        const fallbackTerm = data.months || 12;
        setMinimumTerm(fallbackTerm);
        // Update contract length to match minimum term
        setContractData(prev => ({
          ...prev,
          contractLength: fallbackTerm.toString()
        }));
      } else {
        // Default to 12 months if nothing is found
        setMinimumTerm(12);
        setContractData(prev => ({
          ...prev,
          contractLength: '12'
        }));
      }
    } catch (error) {
      console.error('Error loading minimum term:', error);
      // Default to 12 months on error
      setMinimumTerm(12);
      setContractData(prev => ({
        ...prev,
        contractLength: '12'
      }));
    }
  };

  const findLeadId = async () => {
    if (leadId) {
      setResolvedLeadId(leadId);
      return;
    }

    if (!currentUser?.email) {
      console.log('No current user email available');
      return;
    }

    try {
      console.log('Attempting to find leadId for user:', currentUser.email);
      
      let accountsSnapshot = await getMyAccountSnapshot('Leads');
      
      if (!accountsSnapshot.empty) {
        const leadAccount = accountsSnapshot.docs[0];
        console.log('Found lead account with ID:', leadAccount.id);
        setResolvedLeadId(leadAccount.id);
      } else {
        console.log('No lead account found for user:', currentUser.email);
      }
    } catch (error) {
      console.error('Error finding lead account:', error);
    }
  };


  const handleContractChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    
    setContractData(prev => ({
      ...prev,
      [name]: inputValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      if (currentStep === 3) setCheckoutError(null);
      setCurrentStep(currentStep - 1);
    }
  };

  const skipToStep3 = () => {
    if (isDevelopment) {
      setCurrentStep(3);
    }
  };

  /**
   * Dev-only shortcut that fakes the end state of a completed Stripe checkout: converts the lead
   * to a client on the selected program and drops you on the portal's pending-start dashboard.
   *
   * This deliberately bypasses Stripe, so it also bypasses the payment check. In the real flow
   * nothing here runs in the browser — verifyCheckoutSession and the checkout.session.completed
   * webhook do the conversion server-side only after Stripe confirms the session actually
   * completed. Stripped from production builds by the isDevelopment guard.
   */
  const skipToCheckoutCompleted = async () => {
    if (!isDevelopment) return;
    const companyId = leadId || resolvedLeadId;
    if (!companyId) {
      setCheckoutError('Dev skip: no lead account linked to this checkout.');
      return;
    }
    if (!selectedProgram?.name) {
      setCheckoutError('Dev skip: no program selected.');
      return;
    }

    setIsRedirecting(true);
    try {
      const account = await getAccountById(companyId);
      const clientStages = ['Custom', 'Lite', 'Plus', 'Pro', 'Hourly', '5 Hours', '10 Hours', 'A La Carte'];
      const stage = clientStages.includes(selectedProgram.name) ? selectedProgram.name : 'Custom';
      const startDate = contractData?.startDate ? parseLocalDate(contractData.startDate) : new Date();
      const months = parseInt(contractData?.contractLength || minimumTerm || 12, 10);
      const contractEnd = new Date(startDate);
      contractEnd.setMonth(contractEnd.getMonth() + months);

      if (account?.type !== 'Clients') {
        // Matches convertAccountToClient: the collection carries over on the same account doc,
        // with department/visibility backfilled on older lead uploads.
        const carriedDocuments = normalizeCollectionForConversion(account?.customDocuments);
        await updateAccount(companyId, {
          ...(carriedDocuments ? { customDocuments: carriedDocuments } : {}),
          ...leadConversionFields(account, { by: currentUser?.email || 'Dev Skip', source: 'dev-skip', program: stage }),
          type: 'Clients',
          stage,
          substatus: null,
          result: 'Won',
          program: programMapWith(account?.program, { status: stage, startDate }),
          contractStart: startDate,
          contractEnd,
          // Marks this account as converted without a real payment so test rows are easy to spot.
          devSimulatedCheckout: true,
          statusHistory: [
            ...(account?.statusHistory || []),
            {
              status: stage,
              previousStatus: account?.stage || null,
              date: new Date().toISOString(),
              updatedBy: currentUser?.email || 'Dev Skip',
              action: `Converted to Client - Program: ${stage} (dev skip, no payment)`,
            },
          ],
        });
      }

      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        const existing = userDoc.exists() ? userDoc.data()?.startFlowProgress || {} : {};
        await updateDoc(userRef, {
          startFlowProgress: {
            ...existing,
            checkoutCompleted: true,
            contractSigned: true,
            lastUpdated: new Date().toISOString(),
          },
          lastUpdated: new Date().toISOString(),
        });
      }

      sessionStorage.removeItem('checkoutPersistedData');
      sessionStorage.removeItem('checkoutReturnToStep3');
      navigate('/portal', { replace: true });
    } catch (err) {
      console.error('Dev skip to checkout completed failed:', err);
      setCheckoutError(err.message || 'Dev skip failed');
      setIsRedirecting(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 2) {
      // Contract validation
      if (!contractData.startDate) {
        newErrors.startDate = 'Start date is required';
      } else {
        const selectedDate = parseLocalDate(contractData.startDate);
        
        // Check if date is a weekday
        if (isWeekend(selectedDate)) {
          newErrors.startDate = 'Start date must be a weekday (Monday-Friday)';
        }
        
        // Check if date is at least 1 weekday out
        if (isBeforeMinStartDate(selectedDate)) {
          newErrors.startDate = 'Start date must be at least 1 weekday from today';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      nextStep();
    }
  };

  const getBillingCycleText = () => {
    const cycleMap = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      annual: 'Annual',
    };
    return cycleMap[billingCycle] || billingCycle;
  };

  const getBillingDaysText = () => {
    if (billingCycle === 'quarterly') return '90';
    if (billingCycle === 'annual') return '365';
    return '30';
  };

  const getMonthlyPriceText = () => {
    const basePrice = selectedProgram?.pricePerMonth || 0;
    if (billingCycle === 'quarterly') {
      return `$${Math.round(basePrice * 3 * 0.9)} per quarter`;
    }
    if (billingCycle === 'annual') {
      const discount = selectedTier === 'Custom' ? 0.9 : 0.8;
      return `$${Math.round(basePrice * 12 * discount)} per year`;
    }
    return `$${Math.round(basePrice)} per month`;
  };

  const loadProgramItemsText = async () => {
    const customDeliverables = location.state?.customDeliverables;
    const customDeliverableQuantities = location.state?.customDeliverableQuantities;

    if (customDeliverables && Array.isArray(customDeliverables) && customDeliverables.length > 0) {
      try {
        const [alaCarteSnapshot, bidPrices] = await Promise.all([
          getDocs(collection(db, 'alaCarteItems')),
          getAccountBidPrices(leadId || resolvedLeadId),
        ]);
        const alaCarteItems = alaCarteSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const quantities = customDeliverableQuantities || {};
        const lines = customDeliverables
          .map((itemId) => {
            const item = alaCarteItems.find((i) => i.id === itemId);
            // Same rule as standard programs: deliverables only
            if (!item || !item.hasDeliverable) return null;
            // Still "to be priced" — preliminary, so it goes out for bid instead of
            // onto the contract's program items.
            if (isUnpricedCaseByCaseItem(item, bidPrices)) return null;
            const qty = quantities[itemId] ?? 1;
            const name = item.name || item.itemName || item.title || 'Item';
            if (qty === '0' || qty === 0) return name;
            return `${name} (${qty})`;
          })
          .filter(Boolean);
        if (lines.length > 0) return lines.join(', ');
      } catch (err) {
        console.error('Error loading custom deliverables:', err);
      }
    }

    // Standard programs: list the program's features that are actually deliverables.
    // A feature counts only when it maps to an alaCarteItem with hasDeliverable that
    // applies to this program — the same rule the deliverables tracker uses. The old
    // programs/{id}/contractItems subcollection listed non-deliverable lines too.
    if (selectedProgram?.id || selectedProgram?.name) {
      try {
        const programKey = selectedProgram.id || selectedProgram.name;
        const [programSnap, alaCarteSnapshot, bidPrices] = await Promise.all([
          getDoc(doc(db, 'programs', programKey)),
          getDocs(collection(db, 'alaCarteItems')),
          getAccountBidPrices(leadId || resolvedLeadId),
        ]);
        const features = programSnap.exists() ? programSnap.data().features || {} : {};
        const alaCarteItems = alaCarteSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        const items = Object.keys(features)
          .map((featureName) => {
            const match = alaCarteItems.find(
              (item) =>
                item.name === featureName &&
                item.hasDeliverable &&
                itemDeliverableAppliesToProgram(item, programKey)
            );
            if (!match) return null;
            // Unpriced case-by-case features stay off the contract too.
            if (isUnpricedCaseByCaseItem(match, bidPrices)) return null;
            // quantity '0' is checkbox mode — included, but shown without a count
            const rawQty = features[featureName]?.quantity;
            if (rawQty === '0' || rawQty === 0) return featureName;
            const qty = parseInt(rawQty, 10) || 1;
            return `${featureName} (${qty})`;
          })
          .filter(Boolean);

        if (items.length > 0) return items.join(', ');
      } catch (err) {
        console.error('Error loading program deliverables:', err);
      }
    }

    return selectedProgram?.name || '';
  };

  const handleSignContract = async () => {
    if (!validateStep(2)) return;
    if (!currentUser?.email) {
      setCheckoutError('You must be logged in to sign the contract.');
      return;
    }

    let formData = signerFormData;
    if (!formData) {
      try {
        formData = await buildSignerFormDataFromProfile(currentUser);
        setSignerFormData(formData);
      } catch (err) {
        console.error('Error loading signer profile:', err);
        setCheckoutError('Could not load your profile information. Please try again.');
        return;
      }
    }

    // Merge anything typed into the inline form over what the profile supplied.
    const merged = { ...formData };
    for (const { key } of SIGNER_FORM_FIELDS) {
      const typedValue = signerFieldInputs[key];
      if (typedValue != null && String(typedValue).trim() !== '') {
        merged[key] = String(typedValue).trim();
      }
    }

    const missingKeys = SIGNER_FORM_FIELDS.filter(({ key }) => {
      const value = merged[key];
      return value == null || String(value).trim() === '';
    });

    if (missingKeys.length > 0) {
      // Prompt for the gaps rather than turning them away — most signers have no idea
      // where "profile information" lives, and the NDA may not have synced yet.
      setShowSignerFields(true);
      setSignerFieldErrors(
        Object.fromEntries(missingKeys.map(({ key }) => [key, true]))
      );
      setCheckoutError(
        showSignerFields
          ? 'Please fill in the highlighted fields to continue.'
          : 'We need a few details for your contract before you sign.'
      );
      return;
    }

    formData = merged;
    setSignerFormData(merged);
    setSignerFieldErrors({});

    // Remember what they typed so neither this checkout nor the next one asks again.
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        signerProfile: merged,
        lastUpdated: new Date().toISOString(),
      });
    } catch (err) {
      // Non-fatal: the values are already in hand for this envelope.
      console.error('Could not save signer profile:', err);
    }

    setIsSigningContract(true);
    setCheckoutError(null);

    try {
      const contractNumber = await getNextContractNumber();
      const programItems = await loadProgramItemsText();
      const isPortal = location.pathname.startsWith('/portal');
      const finalLeadId = leadId || resolvedLeadId;

      const checkoutContext = {
        isPortal,
        leadId: finalLeadId || null,
        programName: selectedProgram.name,
        programId: selectedProgram.id || '',
        tier: selectedTier,
        billingCycle: getBillingCycleText(),
        billingDays: getBillingDaysText(),
        startDate: contractData.startDate,
        contractLength: contractData.contractLength,
        minimumTerm: String(minimumTerm),
        contractNumber: String(contractNumber),
        programItems,
        monthlyPrice: getMonthlyPriceText(),
        priceText: `$${getPrice()} ${getBillingText()}`,
        // Matches the "Total" card on the confirm step exactly
        paymentAmount: `$${getPrice().toLocaleString('en-US')}`,
      };

      sessionStorage.setItem('checkoutPersistedData', JSON.stringify({
        selectedProgram,
        selectedTier,
        billingCycle,
        leadId: finalLeadId,
        contractData,
        customDeliverables: location.state?.customDeliverables,
        customDeliverableQuantities: location.state?.customDeliverableQuantities,
      }));

      const createEnvelope = httpsCallable(functions, 'createContractEnvelope');
      const result = await createEnvelope({
        formData: {
          signerName: formData.signerName,
          signerTitle: formData.signerTitle,
          title: formData.signerTitle,
          companyName: formData.companyName,
          companyAddress: formData.companyAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          businessStructure: formData.businessStructure,
        },
        signerEmail: currentUser.email,
        checkoutContext,
      });

      const { envelopeId, signingUrl } = result.data;

      await updateDoc(doc(db, 'users', currentUser.uid), {
        pendingContract: {
          envelopeId,
          startedAt: new Date().toISOString(),
        },
        lastUpdated: new Date().toISOString(),
      });

      await incrementContractNumber();
      window.location.href = signingUrl;
    } catch (err) {
      console.error('createContractEnvelope error:', err);
      setCheckoutError(err.message || 'Failed to start contract signing. Please try again.');
      setIsSigningContract(false);
    }
  };

  if (!selectedProgram) {
    return (
      <div className="min-h-screen relative flex items-center justify-center">
        {/* Same ambient BBA background the Start Dashboard shows */}
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading checkout...</p>
        </div>
      </div>
    );
  }

  const getPrice = () => {
    const basePrice = selectedProgram.pricePerMonth || 0;
    const annualDiscount = selectedTier === 'Custom' ? 0.9 : 0.8; // Custom: 10% off; others: 20% off
    switch (billingCycle) {
      case 'quarterly':
        return Math.round(basePrice * 3 * 0.9); // 10% discount
      case 'annual':
        return Math.round(basePrice * 12 * annualDiscount);
      default:
        return Math.round(basePrice);
    }
  };

  const getBillingText = () => {
    switch (billingCycle) {
      case 'quarterly':
        return 'per quarter';
      case 'annual':
        return 'per year';
      default:
        return 'per month';
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Same ambient BBA background the Start Dashboard shows */}
      <PortalBackground />
      {/* Dev mode: Skip to Step 3 / straight to the converted-client end state */}
      {isDevelopment && (
        <div className="fixed top-20 right-4 z-50 flex flex-col items-end gap-2">
          <button
            onClick={skipToStep3}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-lg font-medium text-sm transition-colors"
            title="Skip to Step 3 (dev only)"
          >
            <FaCode className="h-4 w-4" />
            Skip to Step 3
          </button>
          <button
            onClick={skipToCheckoutCompleted}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg shadow-lg font-medium text-sm transition-colors"
            title="Convert this lead to a client and open the portal, skipping Stripe entirely (dev only)"
          >
            <FaCode className="h-4 w-4" />
            Skip to Checkout Completed
          </button>
        </div>
      )}
      <StartSidebar />
      <div className="relative py-8 px-4">
        <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => {
              const isPortal = location.pathname.startsWith('/portal');
              if (isPortal) {
                // Go back to the program they were configuring — the history
                // entry keeps /portal/programdetail's router state, which a
                // fresh navigation would lose (bouncing them to billing).
                if (window.history.state?.idx > 0) {
                  navigate(-1);
                } else {
                  navigate('/portal/billing');
                }
              } else {
                navigate('/start/program');
              }
            }}
            className="inline-flex items-center justify-center w-10 h-10 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <FaArrowLeft className="h-4 w-4" />
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Complete Signup
          </h1>
          
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>

        {/* Step Progress Indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-8">
            {/* Step 1 */}
            <div className="flex items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                currentStep >= 1 ? 'bg-red-600' : 'bg-gray-300'
              }`}>
                1
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${currentStep >= 1 ? 'text-red-600' : 'text-gray-500'}`}>
                  Confirm Program
                </p>
              </div>
            </div>
            
            {/* Connector */}
            <div className={`w-16 h-1 ${currentStep >= 2 ? 'bg-red-600' : 'bg-gray-300'}`}></div>
            
            {/* Step 2 */}
            <div className="flex items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                currentStep >= 2 ? 'bg-red-600' : 'bg-gray-300'
              }`}>
                2
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${currentStep >= 2 ? 'text-red-600' : 'text-gray-500'}`}>
                  Start Date & Contract
                </p>
              </div>
            </div>
            
            {/* Connector */}
            <div className={`w-16 h-1 ${currentStep >= 3 ? 'bg-red-600' : 'bg-gray-300'}`}></div>
            
            {/* Step 3 - Checkout */}
            <div className="flex items-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                currentStep >= 3 ? 'bg-red-600 text-white' : 'text-gray-400 bg-gray-300 dark:bg-gray-600'
              }`}>
                3
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${currentStep >= 3 ? 'text-red-600' : 'text-gray-500'}`}>
                  Checkout
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Step Content */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
            {/* Step 1: Confirm Program */}
            {currentStep === 1 && (
                <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Confirm Your Program</h2>
                
                {/* No program-name card: every program is custom, so it would always read "Custom". */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {/* Monthly Total Card */}
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 text-center">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                      {billingCycle === 'monthly' ? 'Monthly Total' : 'Total'}
                    </h3>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">${getPrice()}</p>
                          </div>
                  
                  {/* Billing Cycle Card */}
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 text-center">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Billing Cycle</h3>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">{billingCycle}</p>
                          </div>
                  
                  {/* Minimum Term Card */}
                  <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 text-center">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Minimum Term</h3>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{minimumTerm} months</p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Start Date and Contract */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Start Date & Contract</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                      Program Start Date *
                    </label>
                    <StartDateCalendar
                      value={contractData.startDate}
                      onSelect={(selectedDateStr) => {
                        setErrors(prev => ({
                          ...prev,
                          startDate: ''
                        }));
                        setContractData(prev => ({
                          ...prev,
                          startDate: selectedDateStr
                        }));
                      }}
                    />
                    {errors.startDate && <p className="text-red-500 text-sm mt-1 text-center">{errors.startDate}</p>}
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                      Start date must be a weekday (Monday-Friday) and at least 1 weekday from today.
                    </p>
                  </div>

                  {/* Billing Start Notice */}
                  <div className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CalendarCheck size={18} className="text-red-600" />
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Billing Starts</h3>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {contractData.startDate
                        ? `You will not be billed until ${formatDisplayDate(contractData.startDate)}`
                        : 'You will not be billed until your program start date'}
                    </p>
                  </div>

                  {showSignerFields && (
                    <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800/40">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1 text-center">
                        Your Contract Details
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 text-center">
                        These appear on your agreement. We&apos;ll save them for next time.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {SIGNER_FORM_FIELDS.map(({ key, label }) => {
                          const value =
                            signerFieldInputs[key] ?? signerFormData?.[key] ?? '';
                          const invalid = signerFieldErrors[key] && !String(value).trim();
                          const inputClass = `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
                            invalid
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                          } bg-white dark:bg-gray-800 text-gray-900 dark:text-white`;
                          const onChange = (e) => {
                            const next = e.target.value;
                            setSignerFieldInputs((prev) => ({ ...prev, [key]: next }));
                            if (next.trim()) {
                              setSignerFieldErrors((prev) => {
                                if (!prev[key]) return prev;
                                const rest = { ...prev };
                                delete rest[key];
                                return rest;
                              });
                            }
                          };
                          return (
                            <div key={key}>
                              <label
                                htmlFor={`signer-${key}`}
                                className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                              >
                                {label}
                              </label>
                              {key === 'businessStructure' ? (
                                <select
                                  id={`signer-${key}`}
                                  value={value}
                                  onChange={onChange}
                                  className={inputClass}
                                >
                                  <option value="">Select…</option>
                                  {BUSINESS_STRUCTURE_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  id={`signer-${key}`}
                                  type="text"
                                  value={value}
                                  onChange={onChange}
                                  className={inputClass}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {checkoutError && currentStep === 2 && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300 text-sm">
                      {checkoutError}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Hosted Stripe Checkout (redirect) */}
            {currentStep === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Payment</h2>
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedProgram.name} — ${getPrice()}
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                      ({getBillingText()})
                    </span>
                  </p>
                </div>
                {checkoutError && (
                  <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300">
                    {checkoutError}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handlePayNow('ach')}
                    disabled={isRedirecting}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors flex flex-col items-center gap-2 ${
                      isRedirecting
                        ? 'border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed dark:border-gray-600 dark:bg-gray-700'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <FaUniversity className="w-6 h-6" />
                    <span>{isRedirecting ? 'Redirecting to Stripe...' : 'Pay with Bank Account (ACH)'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePayNow('card')}
                    disabled={isRedirecting}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-colors flex flex-col items-center gap-2 ${
                      isRedirecting
                        ? 'border-gray-300 bg-gray-300 text-gray-500 cursor-not-allowed dark:border-gray-600 dark:bg-gray-700'
                        : 'border-red-500 bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    <FaCreditCard className="w-6 h-6" />
                    <span>{isRedirecting ? 'Redirecting to Stripe...' : 'Pay with Credit Card (3% Fee)'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  currentStep === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                Previous
              </button>
              
              {currentStep < 2 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  Continue
                </button>
              ) : currentStep === 3 ? (
                <div className="w-24" /> // Spacer - Pay button is in the form
              ) : (
                <button
                  type="button"
                  onClick={handleSignContract}
                  disabled={!contractData.startDate || isSigningContract}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    contractData.startDate && !isSigningContract
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isSigningContract ? 'Redirecting to DocuSign…' : 'Sign Contract with DocuSign'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      </div>
    </div>
  );
};

export default Checkout;